import { TrendingUp, AlertTriangle, Minus } from 'lucide-react';
import { calculateRampRate, getRampRateRisk, type RampRisk } from '@/lib/analytics-calculations';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface RampRateCardProps {
  ctlArray: number[];
  rampRateOverride?: number;
}

const riskConfig: Record<RampRisk, { color: string; bg: string; border: string; icon: typeof TrendingUp }> = {
  low: { color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20', icon: Minus },
  moderate: { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20', icon: TrendingUp },
  high: { color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20', icon: AlertTriangle },
  critical: { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20', icon: AlertTriangle },
};

export function RampRateCard({ ctlArray, rampRateOverride }: RampRateCardProps) {
  const rampRate =
    typeof rampRateOverride === 'number' && Number.isFinite(rampRateOverride)
      ? rampRateOverride
      : calculateRampRate(ctlArray);
  const risk = getRampRateRisk(rampRate);
  const cfg = riskConfig[risk];
  const Icon = cfg.icon;

  return (
    <div className={`rounded-xl border p-4 ${cfg.border} ${cfg.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
          Ramp Rate <MetricInfoButton metricKey="rampRate" />
        </span>
        <Icon className={`h-4 w-4 ${cfg.color}`} />
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold tabular-nums ${cfg.color}`}>
          {rampRate >= 0 ? '+' : ''}{rampRate.toFixed(1)}
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">CTL/week</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <div className={`h-1.5 w-1.5 rounded-full ${risk === 'low' ? 'bg-emerald-400' : risk === 'moderate' ? 'bg-amber-400' : risk === 'high' ? 'bg-orange-400' : 'bg-red-400'}`} />
        <span className="text-[10px] font-mono text-muted-foreground capitalize">{risk} risk</span>
      </div>
    </div>
  );
}
